"use client";

import { Input as InputPrimitive } from "@base-ui/react/input";
import * as React from "react";
import { cn } from "@/lib/utils";

export type InputProps = Omit<
  InputPrimitive.Props & React.RefAttributes<HTMLInputElement>,
  "size"
> & {
  size?: "sm" | "default" | "lg" | number;
  unstyled?: boolean;
  nativeInput?: boolean;
};

export const Input = React.forwardRef<HTMLInputElement, InputProps>(function Input(
  { className, size = "default", unstyled = false, nativeInput = false, style, ...props },
  ref,
): React.ReactElement {
  const controlHeightClassName = size === "sm" ? "h-8" : size === "lg" ? "h-10" : "h-9";
  const inputClassName = cn(
    "w-full min-w-0 rounded-[inherit] px-3 leading-normal outline-none focus:outline-none focus-visible:outline-none active:outline-none [transition:background-color_5000000s_ease-in-out_0s] placeholder:text-muted-foreground",
    unstyled ? controlHeightClassName : "h-full",
    size === "sm" && "px-2.5",
    props.type === "search" &&
      "[&::-webkit-search-cancel-button]:appearance-none [&::-webkit-search-decoration]:appearance-none [&::-webkit-search-results-button]:appearance-none [&::-webkit-search-results-decoration]:appearance-none",
    props.type === "file" &&
      "text-muted-foreground file:me-3 file:bg-transparent file:font-medium file:text-foreground file:text-sm",
  );

  return (
    <span
      className={
        cn(
          !unstyled &&
            cn(
              "relative inline-flex w-full rounded-[var(--paper-radius-control)] border border-control-border bg-control-default text-sm text-foreground transition-[border-color,box-shadow,background-color] hover:border-control-border-hover hover:bg-control-highlight has-focus-visible:border-ring has-focus-visible:bg-control-default has-focus-visible:ring-2 has-focus-visible:ring-ring/15 has-focus-visible:has-aria-invalid:border-destructive has-focus-visible:has-aria-invalid:ring-destructive/15 has-aria-invalid:border-destructive has-aria-invalid:ring-2 has-aria-invalid:ring-destructive/15 has-autofill:bg-foreground/4 has-disabled:bg-muted/60 has-disabled:opacity-60",
              controlHeightClassName,
              size === "sm" && "text-xs",
            ),
          className,
        ) || undefined
      }
      data-size={size}
      data-slot="input-control"
    >
      {nativeInput ? (
        <input
          ref={ref}
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          style={typeof style === "function" ? undefined : style}
          {...props}
        />
      ) : (
        <InputPrimitive
          ref={ref}
          className={inputClassName}
          data-slot="input"
          size={typeof size === "number" ? size : undefined}
          style={style}
          {...props}
        />
      )}
    </span>
  );
});

export { InputPrimitive };
